import type {StateCreator} from 'zustand'
import type {DiffRange} from '@github-ui/diff-lines/types'

export type ComparisonInfo = {
  fullDiff: DiffRange
  /**
   * The explicitly selected commit range, or undefined when viewing all commits (FULL mode).
   * Use this when you need to know if a specific commit range was selected by the user.
   */
  selectedRange: DiffRange | undefined
  /**
   * The effective range to use for the current comparison.
   * This always has a value - it falls back to fullDiff when no specific range is selected.
   * Use this when you always need base/head OIDs regardless of comparison mode.
   */
  effectiveRange: DiffRange
  hideWhitespace: boolean
  comparisonView: 'FULL' | 'COMMIT' | 'RANGE'
}

export interface ComparisonInfoSlice {
  comparisonInfo: ComparisonInfo
  comparisonInfoActions: {
    updateWhitespace: (newVal: boolean) => void
  }
}

export interface ComparisonInfoSliceOpts {
  baseOid: string
  headOid: string
  selectedCommitRange?: {
    baseOid: string
    headOid: string
  }
  comparisonView: 'FULL' | 'COMMIT' | 'RANGE'
  hideWhitespace: boolean
}

export const createComparisonInfoSlice =
  ({
    baseOid,
    headOid,
    selectedCommitRange,
    comparisonView,
    hideWhitespace,
  }: ComparisonInfoSliceOpts): StateCreator<ComparisonInfoSlice, [['zustand/devtools', never]]> =>
  set => {
    return {
      comparisonInfo: {
        fullDiff: {
          baseOid,
          headOid,
        },
        selectedRange: selectedCommitRange,
        effectiveRange: selectedCommitRange ?? {baseOid, headOid},
        hideWhitespace,
        comparisonView,
      },
      comparisonInfoActions: {
        updateWhitespace: (newVal: boolean) =>
          set(
            (prevState: ComparisonInfoSlice) => ({
              comparisonInfo: {
                ...prevState.comparisonInfo,
                hideWhitespace: newVal,
              },
            }),
            undefined,
            'comparisonInfo#updateWhitespace',
          ),
      },
    }
  }
